import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import Classifier from "models/ClassifierModel";
import { ServiceError } from "services/BaseService";
import {
    ClassificationService,
    LlmReclassifyTokenEstimate,
} from "services/ClassificationService";
import { pricingForModel } from "components/Admin/Organizations/OrganizationDetail/KboFlowDebug/kboModelPricing";
import { DatePickerComponentStore } from "stores/ComponentStores/DatePickerComponentStore";
import { DialogComponentStore } from "stores/ComponentStores/DialogComponentStore";
import { OrgSelectorComponentStore } from "stores/ComponentStores/OrgSelectorComponentStore";
import NotificationStore, {
    INotificationTransformFactory,
} from "stores/Notifications/NotificationStore";
import {
    NotificationArgument,
    NotificationDetails,
    NotificationType,
} from "stores/RealTimeMessages/MessageStoreBase";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { v4 as uuidv4 } from "uuid";
import { AuthStore } from "stores/AuthStore";
import ClassifierService from "services/ClassifierService";
import { BaseStore } from "stores/BaseStore";
import { ClassifierBuilderV2Store } from "./ClassifierBuilderV2Store";

export type ReclassifyClassifierType = "Lucene" | "LLM";

/** The fields the dialog uses, so both Lucene classifiers and LLM models can be selected. */
export type ReclassifyClassifierOption = Pick<
    Classifier,
    "id" | "name" | "isPublished"
>;

// LLM reclassify ETA. LLM_AMDS_PER_BATCH mirrors MaxAmdsPerLLMBatch in ReclassifyJobOptions (ReclassifyService).
// LLM_BATCHES_PER_MINUTE is the throughput the scaled-up lane is expected to sustain end to end. It is below the
// dispatch limit (MaxLLMMessagesPerMinute, 50), so the lane is the bottleneck. At about 45 s per batch it implies
// roughly 11 batches in flight. LLM_STARTUP_SECONDS covers what that steady-state figure leaves out, mostly for small
// jobs: the bulk lane scaling up from zero (autoscaler delay, pod start) and the first and last batch latency while
// only a few replicas are running. Both figures are deliberately conservative: an over-estimate is better than an
// under-estimate.
const LLM_AMDS_PER_BATCH = 50;
const LLM_BATCHES_PER_MINUTE = 15;
const LLM_STARTUP_SECONDS = 300;

const classificationService = new ClassificationService();

class ReclassifyNotificationArg {
    totalCount: number;
    completedCount: number;
    type: NotificationType;
    timestamp: string;
    message: string;
    title: string;
    id: string;
    addtlDetails?: NotificationDetails;
}

@AcxStore
class ClassifierBuilderV2ReclassifyDialogStore extends BaseStore {
    private readonly classifierService = new ClassifierService();

    resetDialog = () => {
        this.loadingAffected = false;
        this.succeeded = -1;
        this.failed = -1;
        this.dialog.setNotLoading();
        this.confirmDialog.setNotLoading();
    };
    notificationStore: NotificationStore;

    dialog = new DialogComponentStore(this.resetDialog);
    confirmDialog = new DialogComponentStore(this.resetDialog);

    datePickerStore = new DatePickerComponentStore();
    orgSelectStore: OrgSelectorComponentStore;
    public readonly authStore: AuthStore;

    @observable
    loadingAffected = false;

    @observable
    numAffectedInteractions: number;

    // LLM only: each model's recent average tokens per run times the AMD count, from CheckReclassificationV2
    @observable
    tokenEstimate: LlmReclassifyTokenEstimate[] = [];

    // LLM only: selected models with no recent token usage, so they are left out of the estimate
    @observable
    classifiersWithoutUsage: string[] = [];

    @observable
    totalCount: number = 0;

    @observable
    completedCount: number = 0;

    @observable
    succeeded: number = -1;

    @observable
    failed: number = -1;

    @observable
    reclassifySessionInProgress: boolean = false;

    @observable
    selectedClassifiers: ReclassifyClassifierOption[] = [];

    // Each page sets this when it opens the dialog; the store is shared by the Lucene table and the LLM page.
    @observable
    classifierType: ReclassifyClassifierType = "Lucene";

    @observable
    hierarchyIds: string[] = [];

    @observable
    hierarchiesApplied: number = 0;

    @observable
    useV2Reclassify: boolean = false;

    constructor(public rootStore: IRootStore) {
        super("ClassifierBuilderReclassifyDialogStore");

        makeObservable(this);
        this.authStore = this.rootStore.getStore(AuthStore);
        this.orgSelectStore = rootStore.getStore(
            ClassifierBuilderV2Store,
        ).orgSelectStore;
        this.notificationStore = rootStore.getStore(NotificationStore);

        reaction(
            (r) => ({
                orgId: this.authStore.orgStore.selectedOrganization?.id,
            }),
            () => this.setHierarchyIds([]),
            { fireImmediately: true },
        );

        reaction(() => {
            return {
                arg1: [
                    this.datePickerStore.beginDate,
                    this.datePickerStore.endDate,
                ],
                arg3: this.datePickerStore.referenceOption,
                hierarchiesApplied: this.hierarchiesApplied,
                classifierIds: this.classifierIds,
                useV2Reclassify: this.useV2Reclassify,
                classifierType: this.classifierType,
            };
        }, this.checkReclassificationAction);
    }

    @action
    checkReclassificationAction = this.debounceEffect(async () => {
        if (!this.orgSelectStore.orgId) {
            return;
        }

        if (
            this.datePickerStore.beginDate.isAfter(this.datePickerStore.endDate)
        ) {
            return;
        }
        if (
            this.datePickerStore.endDate.isBefore(
                this.datePickerStore.beginDate,
            )
        ) {
            return;
        }

        this.loadingAffected = true;
        const payload = await classificationService.CheckReclassification(
            this.orgSelectStore.orgId,
            this.datePickerStore.beginDate,
            this.datePickerStore.endDate,
            this.datePickerStore.referenceOption,
            this.hierarchyIds,
            this.classifierIds,
            // LLM models are V2-only; for Lucene the Ultra-only toggle decides
            this.classifierType === "LLM" || this.useV2Reclassify,
        );
        runInAction(() => {
            this.numAffectedInteractions = payload.numRecords;
            this.tokenEstimate = payload.tokenEstimate ?? [];
            this.classifiersWithoutUsage = payload.classifiersWithoutUsage ?? [];
            this.loadingAffected = false;
        });
    }, 200);

    @action
    setHierarchiesApplied = () => {
        this.hierarchiesApplied++;
    };

    @action
    toggleV2Reclassify = () => {
        this.useV2Reclassify = !this.useV2Reclassify;
    };

    @action
    setClassifierType = (classifierType: ReclassifyClassifierType) => {
        // The hierarchy selector's tree is unmounted with the dialog, so a hierarchy picked in the other type's
        // dialog would silently narrow the next request while the UI shows nothing selected.
        if (this.classifierType !== classifierType) this.hierarchyIds = [];
        this.classifierType = classifierType;
    };

    @action
    setSelectedClassifiers = (classifiers?: ReclassifyClassifierOption[]) => {
        this.selectedClassifiers = classifiers ?? [];
    };

    @computed
    get reclassifyLimit() {
        return this.classifierType === "LLM" ? 1_000_000 : 10_000_000;
    }

    @computed
    get reclassifyLimitExceeded() {
        // The Lucene limit has always blocked at the limit itself. The LLM limit matches ReclassifyV2 on the
        // server (LlmReclassifyRecordLimit), which allows exactly 1,000,000.
        return this.classifierType === "LLM"
            ? this.numAffectedInteractions > this.reclassifyLimit
            : this.numAffectedInteractions >= this.reclassifyLimit;
    }

    @computed
    get llmReclassifyEstimateSeconds(): number | undefined {
        if (
            this.classifierType !== "LLM" ||
            this.classifierIds.length === 0 ||
            !Number.isFinite(this.numAffectedInteractions) ||
            this.numAffectedInteractions <= 0
        ) {
            return undefined;
        }

        // The server's count is AMDs x models, and each batch holds the AMDs of a single model
        const modelCount = this.classifierIds.length;
        const amdsPerModel = this.numAffectedInteractions / modelCount;
        const batches =
            modelCount * Math.ceil(amdsPerModel / LLM_AMDS_PER_BATCH);
        // The startup allowance, then each batch costs 60 / LLM_BATCHES_PER_MINUTE seconds of lane throughput
        return LLM_STARTUP_SECONDS + batches * (60 / LLM_BATCHES_PER_MINUTE);
    }

    @computed
    get llmReclassifyEstimateText(): string | undefined {
        const seconds = this.llmReclassifyEstimateSeconds;
        if (seconds === undefined) {
            return undefined;
        }

        const minutes = Math.max(1, Math.ceil(seconds / 60));
        if (minutes < 60) {
            return `${minutes} min`;
        }
        return `${Math.floor(minutes / 60)} h${
            minutes % 60 !== 0 ? ` ${minutes % 60} min` : ""
        }`;
    }

    @computed
    get llmReclassifyCostEstimate():
        | { totalUsd: number; totalTokens: number; unpricedModels: string[] }
        | undefined {
        if (
            this.classifierType !== "LLM" ||
            !Number.isFinite(this.numAffectedInteractions) ||
            this.numAffectedInteractions <= 0 ||
            this.tokenEstimate.length === 0
        ) {
            return undefined;
        }

        let totalUsd = 0;
        let totalTokens = 0;
        const unpricedModels: string[] = [];
        for (const estimate of this.tokenEstimate) {
            // Cached input tokens are a subset of input tokens, billed at the cached rate
            const cachedInputTokens = Math.min(
                estimate.cachedInputTokens,
                estimate.inputTokens,
            );
            const uncachedInputTokens = estimate.inputTokens - cachedInputTokens;
            totalTokens += estimate.inputTokens + estimate.outputTokens;

            const pricing = pricingForModel(estimate.modelName);
            if (!pricing) {
                unpricedModels.push(estimate.modelName);
                continue;
            }
            totalUsd +=
                (uncachedInputTokens / 1_000_000) * pricing.input +
                (cachedInputTokens / 1_000_000) * pricing.cachedInput +
                (estimate.outputTokens / 1_000_000) * pricing.output;
        }
        return { totalUsd, totalTokens, unpricedModels };
    }

    @computed
    get selectedPublishedClassifiers() {
        return this.selectedClassifiers.filter(
            (classifier) => classifier.isPublished,
        );
    }

    @computed
    get classifierIds() {
        return this.selectedClassifiers
            ? this.selectedClassifiers.map((classifier) => classifier.id)
            : [];
    }

    @computed
    get publishedClassifierIds() {
        const res = this.selectedClassifiers
            ? this.selectedClassifiers
                  .filter((classifier) => classifier.isPublished)
                  .map((classifier) => classifier.id)
            : [];
        return res;
    }

    @action
    setHierarchyIds = (selectedBranchIds: string[]) => {
        this.hierarchyIds = selectedBranchIds;
    };

    @action
    setReclassifySessionInProgress = (inProgress: boolean) => {
        this.reclassifySessionInProgress = inProgress;
    };

    @action
    reclassify = async () => {
        // Captured before the awaited request: the user can open the other page's dialog while it is in flight,
        // and the catch below must act on the type this request was sent for.
        const isLlm = this.classifierType === "LLM";
        this.dialog.setLoading();
        this.dialog.setErrorMessage("");

        try {
            var sessionId = uuidv4() as string;
            this.setReclassifySessionInProgress(true);

            await classificationService.Reclassify(
                this.orgSelectStore.orgId as string,
                sessionId,
                this.datePickerStore.beginDate,
                this.datePickerStore.endDate,
                this.datePickerStore.referenceOption,
                this.hierarchyIds,
                this.classifierIds,
                // LLM models are V2-only; for Lucene the Ultra-only toggle decides
                this.classifierType === "LLM" || this.useV2Reclassify,
                this.numAffectedInteractions,
            );

            runInAction(() => {
                this.succeeded = 1;
                this.dialog.setNotLoading();
            });
        } catch (exception: unknown) {
            if (typeof exception === "object" && exception !== null) {
                const exceptionMsg = (exception as Error).message;
                const isEmptyJson: boolean = !exceptionMsg.includes(
                    "Unexpected end of JSON input",
                );
                isEmptyJson ??
                    this.dialog.setErrorMessage("Reclassification Failed");
            }
            if (isLlm) {
                // The dialog is already closed when this runs, so show the server's reason as a message and let
                // the dialog be used again (the in-progress flag is otherwise only cleared by a completion notification).
                this.setReclassifySessionInProgress(false);
                this.rootStore.getStore(MessageStore).logError(
                    // || not ??: errorMessage is "" for an empty response body (e.g. a gateway 5xx)
                    (exception as ServiceError).errorMessage ||
                        "Reclassification failed. Please try again.",
                );
            }
            this.dialog.setNotLoading();
        }
    };

    /**
     * Not Used; just here for reference since this is currently that only notification flow that needs to
     * maintain state
     *
     * sample implementation of using closure for notification-transformer so that you could have
     * multiple running at sametime and wouldn't need to worry about clobbering state in the store
     * since the closure maintains its own localEnv (in this case, the closure gives us completedCount,totalCount
     * @param completedCount
     * @param totalCount
     */
    buildNotificationTransformer: INotificationTransformFactory<ReclassifyNotificationArg> =
        (completedCount: number, totalCount: number) => {
            return (arg: ReclassifyNotificationArg) => {
                const notificationArg = { ...arg } as NotificationArgument;
                if (arg.completedCount == null || arg.totalCount == null) {
                    notificationArg.message = "Reclassify In Progress";
                    notificationArg.type = NotificationType.InProgress;
                } else {
                    completedCount += arg.completedCount;
                    if (arg.totalCount > 0) {
                        totalCount = arg.totalCount;
                    }
                    if (completedCount === totalCount) {
                        notificationArg.type = NotificationType.Success;
                        notificationArg.message = "Reclassify Complete";
                        totalCount = 0;
                        completedCount = 0;
                    } else {
                        notificationArg.type = NotificationType.InProgress;
                        notificationArg.message = `Reclassify Progress: ${(
                            (completedCount / totalCount) *
                            100
                        ).toPrecision(3)}%`;
                    }
                }

                return notificationArg;
            };
        };
}

export default ClassifierBuilderV2ReclassifyDialogStore;
